import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Copy, Check } from "lucide-react";

/**
 * MfaEnroll — guided TOTP enrollment: start → scan QR → verify code → recovery codes.
 * Calls onDone() once MFA is active (after the user acknowledges recovery codes).
 */
export function MfaEnroll({ onDone }) {
  const [step, setStep] = useState("intro"); // intro | scan | recovery
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const begin = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetupData(data);
      setStep("scan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start setup.");
    } finally {
      setLoading(false);
    }
  };

  const activate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/mfa/activate", { code: code.trim() });
      setRecovery(data.recovery_codes || []);
      setStep("recovery");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code.");
    } finally {
      setLoading(false);
    }
  };

  const copyRecovery = () => {
    navigator.clipboard?.writeText(recovery.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (step === "intro") {
    return (
      <div data-testid="mfa-enroll-intro">
        <p className="text-sm leading-relaxed text-ek-ink/60">
          Add a second layer of security. You&apos;ll scan a QR code with an authenticator app
          (Google Authenticator, Authy, 1Password) and enter a 6-digit code each time you sign in.
        </p>
        <button
          type="button"
          onClick={begin}
          disabled={loading}
          data-testid="mfa-begin-button"
          className="ek-btn mt-4 disabled:opacity-60"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
          Set up authenticator
        </button>
      </div>
    );
  }

  if (step === "scan") {
    return (
      <form onSubmit={activate} data-testid="mfa-enroll-scan" className="space-y-4">
        <p className="text-sm text-ek-ink/60">
          1. Scan this QR code in your authenticator app, then enter the 6-digit code it shows.
        </p>
        <div className="flex flex-col items-center gap-3 rounded-xl border border-black/5 bg-white p-4">
          {setupData?.qr && (
            <img src={setupData.qr} alt="MFA QR code" data-testid="mfa-qr" className="h-44 w-44" />
          )}
          <p className="text-center text-xs text-ek-ink/65">
            Can&apos;t scan? Enter this key manually:
            <br />
            <span className="font-mono text-ek-ink/70" data-testid="mfa-secret">{setupData?.secret}</span>
          </p>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ek-ink">6-digit code</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            autoFocus
            data-testid="mfa-activate-code-input"
            placeholder="123456"
            className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-center text-lg tracking-[0.3em] outline-none focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
          />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="mfa-activate-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="mfa-activate-button" className="ek-btn w-full disabled:opacity-60">
          {loading ? "Verifying…" : "Verify & enable"}
        </button>
      </form>
    );
  }

  // recovery
  return (
    <div data-testid="mfa-enroll-recovery" className="space-y-4">
      <div className="flex items-center gap-2 text-green-700">
        <ShieldCheck size={18} />
        <p className="text-sm font-semibold">Two-factor authentication is on.</p>
      </div>
      <p className="text-sm text-ek-ink/60">
        Save these recovery codes somewhere safe. Each can be used once if you lose access to your
        authenticator. They won&apos;t be shown again.
      </p>
      <div className="grid grid-cols-2 gap-2 rounded-xl border border-black/5 bg-ek-canvas p-4" data-testid="mfa-recovery-codes">
        {recovery.map((rc) => (
          <span key={rc} className="rounded bg-white px-2 py-1.5 text-center font-mono text-sm text-ek-ink">{rc}</span>
        ))}
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={copyRecovery} data-testid="mfa-copy-recovery" className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm font-medium hover:bg-ek-purple/5">
          {copied ? <Check size={15} className="text-green-600" /> : <Copy size={15} />} {copied ? "Copied" : "Copy codes"}
        </button>
        <button type="button" onClick={() => onDone?.()} data-testid="mfa-recovery-done" className="ek-btn">
          I&apos;ve saved my codes
        </button>
      </div>
    </div>
  );
}
